import React, { useState } from 'react';
import { Avatar, Typography, Tabs, Image, Spin, Empty, Card, Tag, Button, Tooltip } from 'antd';
import CustomDrawer from "components/shared-components/CustomDrawer";
import { CalendarOutlined, CloseOutlined, GlobalOutlined, LinkOutlined, MessageOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import VideoPlayerModal, { isVideoMedia } from '../../components/VideoPlayerModal';
import TabPane from 'antd/es/tabs/TabPane';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import {
    flattenProfilePages,
    profileConversationKeys,
    profileTotalFromPages,
    useProfileMedia,
    useProfileUrls,
} from 'hooks/api/useProfileConversationMedia';

const { Text } = Typography;

const ProfileModel = (props) => {
    const { name, number, show, toggleProfileModal, parent, api, isMobile = false } = props;
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('media');
    const [videoPlayer, setVideoPlayer] = useState({
        open: false,
        url: '',
        title: '',
    });

    const canFetch = Boolean(show && parent && number);

    const mediaQuery = useProfileMedia({
        api,
        parent,
        child: number,
        enabled: canFetch,
    });

    const urlsQuery = useProfileUrls({
        api,
        parent,
        child: number,
        enabled: canFetch && activeTab === 'urls',
    });

    const mediaFiles = flattenProfilePages(mediaQuery.data);
    const chatUrls = flattenProfilePages(urlsQuery.data);
    const urlsTotal = profileTotalFromPages(urlsQuery.data);

    const handleRefresh = () => {
        if (activeTab === 'media') {
            queryClient.invalidateQueries({
                queryKey: profileConversationKeys.media(parent, number),
            });
            return;
        }

        queryClient.invalidateQueries({
            queryKey: profileConversationKeys.urls(parent, number),
        });
    };

    const isRefreshing = activeTab === 'media'
        ? mediaQuery.isFetching && !mediaQuery.isFetchingNextPage
        : urlsQuery.isFetching && !urlsQuery.isFetchingNextPage;

    const getInitials = (name) => {
        const names = name?.split(' ');
        return names?.map((n) => n[0])?.join('');
    };

    const getColorForName = (name) => {
        const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae'];
        const index = name?.length % colors?.length;
        return colors[index];
    };

    const openVideoPlayer = (url, title) => {
        if (!url) {
            return;
        }
        setVideoPlayer({
            open: true,
            url,
            title: title || 'Video',
        });
    };

    const closeVideoPlayer = () => {
        setVideoPlayer({
            open: false,
            url: '',
            title: '',
        });
    };

    const renderMediaTile = (item, index) => {
        const isVideo = isVideoMedia(item?.file_type, item?.file_name);

        const tileStyle = isMobile
            ? {
                position: 'relative',
                aspectRatio: '1/1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                borderRadius: '8px',
                background: '#f5f5f5',
                width: '100%',
            }
            : {
                position: 'relative',
                display: 'block',
                marginBottom: 8,
                breakInside: 'avoid',
                borderRadius: '8px',
                overflow: 'hidden',
                width: '100%',
            };

        const mediaStyle = isMobile
            ? {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
            }
            : {
                width: '100%',
                height: 'auto',
                display: 'block',
            };

        if (isVideo) {
            return (
                <div
                    key={item.file_name || item.file_url || index}
                    role="button"
                    tabIndex={0}
                    onClick={() => openVideoPlayer(item?.file_url, item?.file_name)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openVideoPlayer(item?.file_url, item?.file_name);
                        }
                    }}
                    style={{
                        ...tileStyle,
                        cursor: 'pointer',
                        background: '#000',
                    }}
                >
                    <video
                        src={item?.file_url}
                        preload="metadata"
                        muted
                        playsInline
                        style={mediaStyle}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0.28)',
                        }}
                    >
                        <PlayCircleOutlined style={{ fontSize: isMobile ? 32 : 40, color: '#fff' }} />
                    </div>
                </div>
            );
        }

        return (
            <div
                key={item.file_name || item.file_url || index}
                style={tileStyle}
            >
                <Image
                    src={item?.file_url}
                    alt={`Media ${index + 1}`}
                    style={mediaStyle}
                />
            </div>
        );
    };

    const UrlPreviewCard = ({ linkItem, index }) => {
        const { preview } = linkItem;
        const hasRichPreview = preview?.has_preview && (preview.title || preview.description || preview.image);

        return (
            <Card
                key={`${linkItem.url}-${linkItem.message_id}-${index}`}
                className="url-preview-card"
                style={{
                    marginBottom: 16,
                    borderRadius: 12,
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden'
                }}
                bodyStyle={{ padding: 0 }}
                hoverable
            >
                {hasRichPreview ? (
                    <div>
                        <div style={{ padding: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                {preview.favicon && (
                                    <img
                                        src={preview.favicon}
                                        alt="favicon"
                                        style={{
                                            width: 16,
                                            height: 16,
                                            marginRight: 8,
                                            borderRadius: 2
                                        }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                )}
                                <Text
                                    type="secondary"
                                    style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}
                                >
                                    {preview.site_name || preview.domain}
                                </Text>
                            </div>

                            {preview.title && (
                                <h4 style={{
                                    margin: '0 0 8px 0',
                                    fontSize: 16,
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                    color: '#262626'
                                }}>
                                    {preview.title}
                                </h4>
                            )}

                            {preview.description && (
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 14,
                                        lineHeight: 1.4,
                                        display: 'block',
                                        marginBottom: 12
                                    }}
                                >
                                    {preview.description}
                                </Text>
                            )}

                            <a
                                href={linkItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: '#1890ff',
                                    fontSize: 13,
                                    textDecoration: 'none',
                                    wordBreak: 'break-all',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <LinkOutlined style={{ marginRight: 6, fontSize: 12 }} />
                                {linkItem.url.length > 50 ?
                                    linkItem.url.substring(0, 50) + '...' :
                                    linkItem.url
                                }
                            </a>

                            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <Tag size="small" color="blue">
                                        <MessageOutlined style={{ fontSize: 10 }} /> Chat
                                    </Tag>
                                </div>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                    {moment(linkItem.sent_at).format('MMM DD, HH:mm')}
                                </Text>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{ padding: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                backgroundColor: '#f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 12,
                                flexShrink: 0
                            }}>
                                {preview?.favicon ? (
                                    <img
                                        src={preview.favicon}
                                        alt="favicon"
                                        style={{ width: 20, height: 20 }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                        }}
                                    />
                                ) : null}
                                <GlobalOutlined
                                    style={{
                                        fontSize: 20,
                                        color: '#999',
                                        display: preview?.favicon ? 'none' : 'block'
                                    }}
                                />
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                                    {preview?.site_name || preview?.domain || 'Link'}
                                </Text>

                                <a
                                    href={linkItem.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#1890ff',
                                        fontSize: 12,
                                        textDecoration: 'none',
                                        wordBreak: 'break-all',
                                        display: 'block',
                                        marginBottom: 8
                                    }}
                                >
                                    {linkItem.url.length > 60 ?
                                        linkItem.url.substring(0, 60) + '...' :
                                        linkItem.url
                                    }
                                </a>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Tag size="small" color="default">
                                        <LinkOutlined style={{ fontSize: 10 }} /> Web Link
                                    </Tag>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        {moment(linkItem.sent_at).format('MMM DD, HH:mm')}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        );
    };

    const urlsTabLabel = urlsQuery.data
        ? `Shared Links (${urlsTotal})`
        : 'Shared Links';

    return (
        <>
            <CustomDrawer
                closable={false}
                title={
                    <div className="d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                        <Link className="profile-name gap-3 d-flex justify-content-start align-items-center">
                            <Avatar
                                size={50}
                                style={{ backgroundColor: getColorForName(name), fontSize: 16 }}
                            >
                                {getInitials(name)}
                            </Avatar>
                            <div className='d-flex flex-column justify-content-start'>
                                <h4 className="mb-0">{name}</h4>
                                <p style={{ fontSize: '0.8rem' }} className="mb-0">{number}</p>
                            </div>
                        </Link>
                        <div className="d-flex align-items-center gap-2">
                            <Tooltip title="Refresh">
                                <Button
                                    type="text"
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefresh}
                                    loading={isRefreshing}
                                    disabled={!canFetch}
                                />
                            </Tooltip>
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={toggleProfileModal}
                            />
                        </div>
                    </div>
                }
                open={show}
                onClose={toggleProfileModal}
                destroyOnClose
                width={!isMobile ? "50vw" : undefined}
                bodyStyle={{ padding: isMobile ? "12px 16px" : "16px" }}
                headerStyle={{ padding: "10px 24px" }}
            >
                <div>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        centered
                    >
                        <TabPane tab="Media" key="media">
                            <div className="media" style={{ maxHeight: isMobile ? 'calc(80vh - 180px)' : 'calc(100vh - 220px)', overflowY: 'auto', overflowX: 'hidden' }}>
                                {mediaQuery.isLoading && mediaFiles.length === 0 ? (
                                    <div className="text-center" style={{ padding: 24 }}>
                                        <Spin />
                                    </div>
                                ) : mediaFiles.length ? (
                                    <>
                                        <div
                                            style={
                                                isMobile
                                                    ? {
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                                        gap: 8,
                                                        padding: '4px',
                                                    }
                                                    : {
                                                        columnWidth: '140px',
                                                        columnGap: 8,
                                                        width: '100%',
                                                        padding: '4px',
                                                    }
                                            }
                                        >
                                            {mediaFiles.map((item, index) => renderMediaTile(item, index))}
                                        </div>
                                        {mediaQuery.hasNextPage && (
                                            <div className="text-center" style={{ marginTop: 16 }}>
                                                <Button
                                                    loading={mediaQuery.isFetchingNextPage}
                                                    onClick={() => mediaQuery.fetchNextPage()}
                                                >
                                                    Load more
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <Empty />
                                )}
                            </div>
                        </TabPane>
                        <TabPane tab={urlsTabLabel} key="urls">
                            <div style={{ maxHeight: isMobile ? 'calc(80vh - 180px)' : 'calc(100vh - 220px)', overflowY: 'auto', overflowX: 'hidden' }}>
                                {urlsQuery.isLoading && chatUrls.length === 0 ? (
                                    <div className="text-center" style={{ padding: 24 }}>
                                        <Spin />
                                    </div>
                                ) : chatUrls.length ? (
                                    <>
                                        {chatUrls.map((linkItem, index) => (
                                            <UrlPreviewCard
                                                key={`${linkItem.url}-${linkItem.message_id}-${index}`}
                                                linkItem={linkItem}
                                                index={index}
                                            />
                                        ))}
                                        {urlsQuery.hasNextPage && (
                                            <div className="text-center" style={{ marginTop: 8 }}>
                                                <Button
                                                    loading={urlsQuery.isFetchingNextPage}
                                                    onClick={() => urlsQuery.fetchNextPage()}
                                                >
                                                    Load more
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <Empty
                                        description="No shared links found"
                                        style={{ marginTop: 60 }}
                                    />
                                )}
                            </div>
                        </TabPane>
                    </Tabs>
                </div>
            </CustomDrawer>
            <VideoPlayerModal
                open={videoPlayer.open}
                onClose={closeVideoPlayer}
                url={videoPlayer.url}
                title={videoPlayer.title}
            />
        </>
    );
};

export default ProfileModel;
